import { mediaUrl } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { CountdownTimer } from "../CountdownTimer";
import { useLang } from "../../context/LanguageContext";
import { MapPin, Calendar, ArrowRight } from "lucide-react";

export function HeroSection({ course, onInfo, onEnroll }) {
  const { t, lang } = useLang();
  return (
    <section data-testid="course-hero" className="relative isolate overflow-hidden bg-[#0B1220] pt-12 md:pt-16 pb-16 md:pb-24">
      <div className="grid-overlay absolute inset-0 pointer-events-none" />
      <div className="absolute -top-40 right-0 h-[620px] w-[620px] rounded-full bg-[#1452F0]/25 blur-[130px] pointer-events-none" />
      <div className="relative px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-x-16 lg:gap-y-10 items-start">
          {/* TOP TEXT BLOCK */}
          <div className="order-1 lg:col-span-5 lg:row-start-1 fade-up">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#5B9DFF]" data-testid="hero-category">{course.category}</span>
            <h1 className="font-heading font-light tracking-tight leading-[0.95] mt-4 text-4xl sm:text-5xl lg:text-6xl silver-text">
              {course.title}
            </h1>
            <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-[#9AA7BD] max-w-xl">
              {course.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#C9D2E3]">
              <span className="inline-flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[#5B9DFF]" strokeWidth={1.5} /> {formatDateTime(course.date_time, lang)}
              </span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#5B9DFF]" strokeWidth={1.5} /> {course.city}
              </span>
            </div>
          </div>

          {/* SPEAKER PHOTO */}
          <div className="order-2 lg:col-span-6 lg:col-start-7 lg:row-start-1 lg:row-span-2 fade-up" style={{ animationDelay: "0.1s" }}>
            <div className="relative">
              <div className="overflow-hidden rounded-sm bg-[#11182B] h-[420px] sm:h-[520px] lg:h-[640px] border border-white/10 glow-azure">
                {course.speaker_photo && (
                  <img
                    data-testid="hero-speaker-photo"
                    src={mediaUrl(course.speaker_photo)}
                    alt={course.speaker_name}
                    className="w-full h-full object-cover object-top"
                  />
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/40 to-transparent p-6 md:p-8">
                <div className="text-white">
                  <div className="font-heading text-3xl md:text-4xl font-light leading-tight" data-testid="hero-speaker-name">
                    {course.speaker_name}
                  </div>
                  <div className="text-sm text-[#5B9DFF] mt-1 tracking-wide">{course.speaker_qualification}</div>
                  {course.speaker_claim && (
                    <p className="mt-3 text-sm text-[#C9D2E3] font-light max-w-md leading-relaxed">
                      “{course.speaker_claim}”
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* COUNTDOWN + PRICE + CTA */}
          <div className="order-3 lg:col-span-5 lg:col-start-1 lg:row-start-2 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="border-t border-white/10 pt-7">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#5B9DFF] block mb-3">{t("hero.starts_in")}</span>
              <CountdownTimer date={course.date_time} variant="hero" light testid="hero-countdown" />
            </div>

            <div className="mt-7 flex items-end gap-8">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#5B9DFF] block mb-1">{t("hero.fee")}</span>
                <span className="font-heading text-3xl text-white" data-testid="hero-price">{course.price}</span>
              </div>
              {course.seats && (
                <div className="pb-1">
                  <span className="text-sm text-[#FF6B6B] font-medium">{course.seats}</span>
                </div>
              )}
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-info-btn"
                onClick={onInfo}
                className="group flex-1 bg-[#1452F0] text-white hover:bg-[#0E3DC0] transition-colors rounded-sm px-7 py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(20,82,240,0.5)]"
              >
                {t("common.request_info")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-enroll-btn"
                onClick={onEnroll}
                className="flex-1 border border-white/30 text-white hover:bg-white hover:text-[#0B1220] transition-colors rounded-sm px-7 py-4 text-sm font-medium tracking-wide"
              >
                {t("common.enroll_online")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
